import { useMemo, useState } from 'react';
import { Panel } from './Panel';
import { useTranslate } from '@tolgee/react';
import { TypeSource } from 'views/Conversation/Sider/ToDoBundle/NewTodo';
import {
  buildResultOptionsFromPolicy,
  isNoOptions,
} from 'views/Conversation/Sider/ToDoBundle/useResultForm';
import { useResultTypeLabel } from 'views/Todo/hooks/useLabel';
import { Input, Popconfirm, Radio } from 'antd';
// import { useEventListener } from 'ahooks';
import posthog from '@leyan/lytics';
import { useUpdateTodoMutation } from 'hooks/business/chat/useTicketMutations';
import type { UseTicketListParams } from 'hooks/business/chat/useTicketMutations';
import { useEventListener } from 'ahooks';

/** 前 10 条解决方案支持快捷键 Ctrl+1~9、Ctrl+0 */
const MAX_HOTKEY_INDEX = 9;

/** 需要二次确认的 Shopee 争议类结果（选后会影响解决方案） */
const SHOPEE_DISPUTE_RESULT_VALUES = ['SUBMITTED_DISPUTE', 'DISPUTE_SUCCESS', 'DISPUTE_FAIL'];
const SHOPEE_DISPUTE_SET = new Set(SHOPEE_DISPUTE_RESULT_VALUES);

type SolutionItemProps = {
  todoId: string;
  index: number;
  value: string;
  label: string;
  currentOperateResult: string;
  onChecked: (value: string) => void;
  needShopeeConfirm: boolean;
  readonly?: boolean;
  disabled?: boolean;
};

const SolutionItem = ({
  todoId,
  index,
  value,
  label,
  currentOperateResult,
  onChecked,
  needShopeeConfirm,
  readonly = false,
  disabled = false,
}: SolutionItemProps) => {
  const { t } = useTranslate();
  const [confirmVisible, setConfirmVisible] = useState(false);

  const hotkeyEnabled = index <= MAX_HOTKEY_INDEX;
  const hotkeyNumber = (index + 1) % 10;
  const hotkeyTips = hotkeyEnabled ? `ctrl+${hotkeyNumber}` : null;

  useEventListener('keydown', (e) => {
    if (readonly || disabled) return;
    const { key, ctrlKey } = e;
    if (!hotkeyEnabled || !ctrlKey || currentOperateResult === value || isNaN(Number(key))) return;
    e.preventDefault();
    if (Number(key) === hotkeyNumber) {
      posthog.capture('$hotKey', { hotKey: `Ctrl+${key}`, todoId });
      if (!needShopeeConfirm) {
        onChecked(value);
      } else if (confirmVisible) {
        onChecked(value);
      } else {
        setConfirmVisible(true);
      }
    } else if (confirmVisible) {
      setConfirmVisible(false);
    }
  });

  const radioLabel = (
    <>
      {label}
      {hotkeyTips && (
        <div className="ml-1 text-xs text-[var(--fett-text-quaternary)] whitespace-nowrap">
          {hotkeyTips}
        </div>
      )}
    </>
  );

  if (needShopeeConfirm) {
    return (
      <Popconfirm
        disabled={currentOperateResult === value}
        visible={confirmVisible}
        onVisibleChange={setConfirmVisible}
        placement="left"
        okText={`${t('phrase.确定')}${hotkeyTips ? ` (${hotkeyTips})` : ''}`}
        title={t('向Shopee争议后，解决方案将自动调整，是否确认修改？')}
        onConfirm={(e) => {
          e?.stopPropagation();
          onChecked(value);
        }}
      >
        <div className="inline-block w-full">
          <Radio
            value={value}
            className="pointer-events-none"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
            onKeyDown={(e) => e.preventDefault()}
            style={{ marginRight: 0, wordBreak: 'break-all' }}
          >
            {radioLabel}
          </Radio>
        </div>
      </Popconfirm>
    );
  }

  return (
    <Radio
      value={value}
      onKeyDown={(e) => e.preventDefault()}
      style={{ marginRight: 0, wordBreak: 'break-all' }}
    >
      {radioLabel}
    </Radio>
  );
};

interface SolutionsProps {
  todo: Todo.TodoInstance;
  task: Todo.Task;
  policies: Todo.Policy[];
  typeSource: TypeSource;
  listParams: UseTicketListParams | null;
  readonly?: boolean;
}

export const Solutions = (props: SolutionsProps) => {
  const { todo, typeSource, listParams, readonly = false } = props;
  const { t } = useTranslate();

  const { getResultTypeLabel } = useResultTypeLabel();
  const { mutate: updateTodo, isLoading } = useUpdateTodoMutation(listParams);
  const [inputValue, setInputValue] = useState(todo.customOperateResult);

  const { type, id } = todo;

  const resultOptions = useMemo(() => {
    if (isNoOptions(todo)) return [];
    const policy = typeSource?.find((e) => e.type === type);
    return buildResultOptionsFromPolicy(policy, getResultTypeLabel);
  }, [typeSource, type, getResultTypeLabel, todo]);

  const handleRadioChange = (value: string) => {
    if (value === todo.operateResult) return;
    const isOther = value === 'OTHER';
    const customOperateResult = isOther ? inputValue : '';
    updateTodo({ id, operateResult: value, customOperateResult });
    setInputValue(customOperateResult);
  };

  const handleInputBlur = () => {
    if (inputValue.trim() === todo.customOperateResult.trim()) {
      return;
    }
    updateTodo({ id, customOperateResult: inputValue });
  };

  const showOtherInput = todo.operateResult === 'OTHER';

  if (readonly) {
    const result =
      todo.operateResult === 'OTHER'
        ? todo.customOperateResult
        : getResultTypeLabel(todo.operateResult);
    if (result) {
      return (
        <Panel title={t('todo_solution')}>
          <div className="p-3 bg-[var(--fett-bg-subtle)] rounded">{result}</div>
        </Panel>
      );
    }
    return null;
  }

  return (
    <Panel title={t('todo_solution')}>
      <Radio.Group
        className="w-full"
        value={todo.operateResult}
        onChange={({ target: { value } }) => handleRadioChange(value)}
        disabled={isLoading}
      >
        <div className="w-full grid grid-cols-2 gap-x-2 gap-y-3">
          {resultOptions.map((each, index) => (
            <SolutionItem
              key={each.value}
              todoId={id}
              index={index}
              value={each.value}
              label={each.label}
              currentOperateResult={todo.operateResult}
              onChecked={handleRadioChange}
              needShopeeConfirm={SHOPEE_DISPUTE_SET.has(each.value)}
              readonly={readonly}
              disabled={isLoading}
            />
          ))}
          <SolutionItem
            todoId={id}
            index={resultOptions.length}
            value="OTHER"
            label={t('其他')}
            currentOperateResult={todo.operateResult}
            onChecked={handleRadioChange}
            needShopeeConfirm={false}
            readonly={readonly}
            disabled={isLoading}
          />
        </div>
      </Radio.Group>
      {showOtherInput && (
        <Input
          className="!mt-3"
          placeholder={t('请输入')}
          value={inputValue}
          onChange={({ target: { value } }) => setInputValue(value)}
          onBlur={handleInputBlur}
        />
      )}
    </Panel>
  );
};
